import { Component, Input, Output, EventEmitter, inject, computed, signal, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonContent, IonActionSheet, PopoverController } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { close, share, create, chatbubble, location, time, business, checkmark, pencil, chevronUp, chevronDown, copy, calendar, mail, call } from 'ionicons/icons';
import { ModalMaterialsSectionComponent } from '../modal-materials-section/modal-materials-section.component';
import { ScheduleComponent } from '../schedule/schedule.component';
import { LeerMasComponent } from '../leer-mas/leer-mas.component';
import { HeaderModalComponent } from '../header-modal/header-modal.component';
import { RecyclingPoint } from '../../../types/recycling-point.interface';
import { CustomModalController } from '../../../shared/services/custom-modal-controller.service';
import { PlatformService } from '../../../shared/services/platform.service';
import { PosthogService } from '../../../shared/services/posthog.service';
import { mapEventsState } from '../../../state/map-events.state';
import { ScreenLayer } from '../../../shared/enums/screen-layer.enum';
import { SwNavigatorService } from '../../../shared/services/sw-navigator.service';
import { PointDataService } from '../../../shared/services/point-data.service';
import { getAddressFromEntity } from '../../../shared/utils/location.utils';
import { ShareService } from '../../../shared/services/share.service';
import { SocialSectionComponent } from '../social-section/social-section.component';
import { ActionButtonsSliderComponent, ActionButton, MODAL_ACTION_BUTTONS } from '../action-buttons-slider/action-buttons-slider.component';
import { ContactInfoComponent, ContactActionEvent } from '../contact-info/contact-info.component';
import { LinksChipsComponent } from '../links-chips/links-chips.component';
import { EventDateRangeComponent } from '../event-date-range/event-date-range.component';
import { TrustSourceBadgeComponent } from '../trust-source-badge/trust-source-badge.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../../shared/services/translation.service';

export interface ModalAction {
  type: 'share' | 'comment' | 'edit' | 'save' | 'contact';
  data?: any;
}

@Component({
  selector: 'app-event-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonContent,
    IonActionSheet,
    ModalMaterialsSectionComponent,
    ScheduleComponent,
    LeerMasComponent,
    HeaderModalComponent,
    SocialSectionComponent,
    ActionButtonsSliderComponent,
    ContactInfoComponent,
    LinksChipsComponent,
    EventDateRangeComponent,
    TrustSourceBadgeComponent,
    TranslatePipe
  ],
  templateUrl: './event-modal.component.html',
  styleUrl: './event-modal.component.scss'
})
export class EventModalComponent implements OnInit, OnChanges {
  private modalCtrl = inject(CustomModalController);
  private platform = inject(PlatformService);
  private swnav = inject(SwNavigatorService);
  private pointDataService = inject(PointDataService);
  private popoverCtrl = inject(PopoverController);
  private posthog = inject(PosthogService);
  private shareService = inject(ShareService);
  private tService = inject(TranslationService);

  // Internal signal for tracking event changes
  private readonly _event$ = signal<RecyclingPoint | null>(null);

  @Input() set event(value: RecyclingPoint | null) {
    this._event$.set(value);
  }
  get event(): RecyclingPoint | null {
    return this._event$();
  }

  @Input() scheduleDays: { code: string; label: string; isOpen: boolean }[] = [];
  @Input() scheduleHoursText: string = '';
  @Input() initialHeight?: number;

  @Output() modalAction = new EventEmitter<ModalAction>();
  @Output() dismiss = new EventEmitter<void>();
  @Output() repointMap = new EventEmitter<{ lat: number; lng: number }>();

  // Input materials (what the event receives)
  readonly inputMaterials = computed(() => {
    const event = this._event$();
    if (!event) return [];
    if (event.materialInput && Array.isArray(event.materialInput) && event.materialInput.length > 0) {
      return event.materialInput;
    }
    if (event.materials && Array.isArray(event.materials) && event.materials.length > 0) {
      return event.materials;
    }
    return [];
  });

  // Output materials (what the event delivers)
  readonly outputMaterials = computed(() => {
    const event = this._event$();
    if (!event) return [];
    if (event.materialOutput && Array.isArray(event.materialOutput) && event.materialOutput.length > 0) {
      return event.materialOutput;
    }
    return [];
  });

  // Address string (computed to avoid recalculation on every change detection)
  readonly addressString = computed(() => {
    const event = this._event$();
    return getAddressFromEntity(event, { excludeCities: [] });
  });

  // Event type label (computed)
  readonly eventTypeLabel = computed(() => {
    const event = this._event$();
    const type = event?.type || event?.entityType;
    switch (type) {
      case 'event':
        return this.tService.instant('eventModal.recyclingEvent');
      default:
        return this.tService.instant('eventModal.event');
    }
  });

  // Action buttons for slider
  actionButtons: ActionButton[] = [...MODAL_ACTION_BUTTONS];

  // Comment state
  showCommentSection: boolean = true;

  // Action sheet state
  isActionSheetOpen: boolean = false;
  actionSheetButtons = [
    {
      text: this.tService.instant('eventModal.shareLocation'),
      icon: 'location',
      handler: () => this.shareLocation()
    },
    {
      text: this.tService.instant('eventModal.shareInfo'),
      icon: 'business',
      handler: () => this.shareInfo()
    },
    {
      text: this.tService.instant('eventModal.copyAddress'),
      icon: 'copy',
      handler: () => this.copyAddress()
    },
    {
      text: this.tService.instant('common.cancel'),
      role: 'cancel'
    }
  ];

  constructor() {
    addIcons({
      close,
      share,
      create,
      chatbubble,
      location,
      time,
      business,
      checkmark,
      pencil,
      chevronUp,
      chevronDown,
      copy,
      calendar,
      mail,
      call
    });
    // Track modal open
    setTimeout(() => {
      this.posthog.init();
      this.posthog.capture('modal_open', { modal: 'event', eventId: this.event?.id });
    }, 0);
  }

  ngOnInit() {
  }

  ngOnChanges() {
    // Signal is already updated via the setter
  }

  // Modal actions
  closeModal() {
    this.posthog.capture('modal_close', { modal: 'event', eventId: this.event?.id });
    this.modalCtrl.dismiss(null, 'close');
  }

  toggleModalHeight() {
    this.posthog.capture('modal_toggle_height', { modal: 'event', eventId: this.event?.id });
    this.modalCtrl.toggleHeight();
  }

  repointMapOnEvent(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    if (!this.event?.position) return;
    
    console.log('🗺️ Repoint button clicked, requesting map repoint via global state');
    
    mapEventsState.requestRecenter({
      lat: this.event.position.lat,
      lng: this.event.position.lng
    });
    
    this.posthog.capture('repoint_map', { eventId: this.event?.id });
  }

  async suggestEdit() {
    if (!this.event) return;

    this.posthog.capture('suggest_edit', { eventId: this.event?.id });

    // Set point data in shared service for edit component
    this.pointDataService.setPointForEdit(this.event);

    this.swnav.navigateObj({ path: `/edit-point/${this.event.id}`, back_path: '/home', layer: ScreenLayer.Edit, typeTransition: 1 });
  }

  // Contact methods
  hasContactInfo(): boolean {
    if (!this.event) return false;
    return !!(this.event.telefono || this.event.sitio_web || this.event.contactPhone || this.event.contactEmail);
  }

  openWebsite() {
    if (this.event?.sitio_web) {
      this.platform.windowOpen(this.event.sitio_web, '_blank');
      this.posthog.capture('event_website_click', { eventId: this.event?.id });
    }
  }

  callPhone() {
    const phone = this.event?.telefono || this.event?.contactPhone;
    if (phone) {
      this.platform.windowOpen(`tel:${phone}`, '_self');
      this.posthog.capture('event_phone_click', { eventId: this.event?.id });
    }
  }

  sendEmail() {
    if (this.event?.contactEmail) {
      window.open(`mailto:${this.event.contactEmail}`, '_self');
      this.posthog.capture('event_email_click', { eventId: this.event?.id });
    }
  }

  // Share methods
  openShareOptions() {
    this.isActionSheetOpen = true;
    this.posthog.capture('event_share_options_open', { eventId: this.event?.id });
  }

  shareLocation() {
    if (!this.event?.position) return;
    const entity = this.shareService.fromRecyclingPoint(this.event as RecyclingPoint);
    this.shareService.shareLocation(entity);
    this.posthog.capture('event_share_location', { eventId: this.event?.id });
  }

  shareInfo() {
    const entity = this.shareService.fromRecyclingPoint(this.event as RecyclingPoint);
    this.shareService.shareInfo(entity);
    this.posthog.capture('event_share_info', { eventId: this.event?.id });
  }

  copyAddress() {
    const entity = this.shareService.fromRecyclingPoint(this.event as RecyclingPoint);
    this.shareService.copyToClipboard(this.shareService.getAddressString(entity));
    this.posthog.capture('event_copy_address', { eventId: this.event?.id });
  }

  // Address helper - uses global location utils (kept for internal use)
  getAddressString(): string {
    return this.addressString();
  }

  /** Show the suggest-edit button only when the event is lacking materials, address, or schedule */
  shouldShowEditButton(): boolean {
    if (!this.event) return false;
    const hasMaterials = (this.event.materialInput?.length || 0) > 0
      || (this.event.materials?.length || 0) > 0;
    const hasAddress = !!(this.event.address?.street || this.event.address?.fullAddress);
    const hasSchedule = !!(this.event.schedules && this.event.schedules.length > 0);
    return !hasMaterials || !hasAddress || !hasSchedule;
  }

  // Event-specific: Date formatting for event dates
  getEventDateRange(): string {
    if (!this.event) return '';

    const startDate = (this.event as any).startDate;
    const endDate = (this.event as any).endDate;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const formatter = new Intl.DateTimeFormat('es-AR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      return `${formatter.format(start)} - ${formatter.format(end)}`;
    } else if (startDate) {
      const start = new Date(startDate);
      return new Intl.DateTimeFormat('es-AR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(start);
    }

    return '';
  }

  getEventTime(): string {
    if (!this.event) return '';

    const startTime = (this.event as any).startTime;
    const endTime = (this.event as any).endTime;

    if (startTime && endTime) {
      return `${startTime} - ${endTime}`;
    } else if (startTime) {
      return `${this.tService.instant('eventModal.from')} ${startTime}`;
    }

    return '';
  }

  hasEventScheduleInfo(): boolean {
    return !!(
      (this.event as any).startsAt ||
      (this.event?.schedules && this.event.schedules.length > 0)
    );
  }

  formatDate(date: Date): string {
    if (!date) return '';
    
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes <= 1 ? 'Hace un momento' : `Hace ${minutes} minutos`;
      }
      return hours === 1 ? 'Hace 1 hora' : `Hace ${hours} horas`;
    }
    if (days === 1) return 'Ayer';
    if (days < 7) return `Hace ${days} días`;
    
    return new Date(date).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  // Get event type label (kept for internal use)
  getEventTypeLabel(): string {
    return this.eventTypeLabel();
  }

  // Get event icon
  getEventIcon(): string {
    return 'assets/icons/event.svg';
  }

  /** Handle contact action from unified contact-info component */
  onContactAction(event: ContactActionEvent) {
    switch (event.type) {
      case 'website':
        this.openWebsite();
        break;
      case 'phone':
        this.callPhone();
        break;
      case 'email':
        this.sendEmail();
        break;
      case 'share':
        this.shareInfo();
        break;
    }
  }

  /** Search this event on Google */
  searchEventOnGoogle() {
    if (!this.event?.name) return;

    const query = encodeURIComponent(`${this.event.name} evento reciclaje`);
    window.open(`https://www.google.com/search?q=${query}`, '_blank');
    this.posthog.capture('search_google', { eventId: this.event?.id });
  }

  /** Handle action slider button clicks */
  onActionButtonClick(actionId: string) {
    switch (actionId) {
      case 'share':
        this.shareInfo();
        break;
      case 'google':
        this.searchEventOnGoogle();
        break;
      case 'edit':
        this.suggestEdit();
        break;
      case 'report':
        this.scrollToReports();
        break;
      case 'save':
        this.toggleSave();
        break;
    }
  }

  /** Scroll to the social/report section and highlight it */
  private scrollToReports() {
    const el = document.querySelector('.status-report-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        el.classList.add('ui-guide');
        el.addEventListener('animationend', () => {
          el.classList.add('ui-guide-out');
          setTimeout(() => {
            el.classList.remove('ui-guide', 'ui-guide-out');
          }, 300);
        }, { once: true });
      }, 400);
    }
    this.posthog.capture('action_report_click', { eventId: this.event?.id });
  }

  /** Toggle save/bookmark */
  private toggleSave() {
    const saveBtn = this.actionButtons.find(b => b.id === 'save');
    if (saveBtn) {
      saveBtn.active = !saveBtn.active;
    }
    this.modalAction.emit({ type: 'save', data: { eventId: this.event?.id, saved: saveBtn?.active } });
    this.posthog.capture('action_save_toggle', { eventId: this.event?.id, saved: saveBtn?.active });
  }
}
